import GeneralHand from '@/services/hands/GeneralHand.js';
import getTextFromGeneralHands from '@/services/text/get-text-from-general-hands/get-text-from-general-hands';
import getHandInfo from '@/services/simulator/get-info-from-combined-cards/get-hand-info';
import setSpecificHandInfo from '@/services/simulator/get-info-from-combined-cards/set-specific-hand-info';
import accountStore from '@/store/modules/account/account';
import utilities from '@/services/utilities/utilities';
import constants from '@/services/constants';
import _ from 'lodash';

export default class GeneralHands {
  constructor() {
    let orderedGeneralHandStrings = constants.handRankings['ten-handed-evolutionary'];
    let generalHands = this._getGeneralHandsFromGeneralHandStrings(orderedGeneralHandStrings);

    this._data = generalHands;
    this.numberOfSpecificHandsInRange = 0;
    this.numberOfPossibleSpecificHands = 1326;
  };

  // constructor helpers
  _getGeneralHandsFromGeneralHandStrings(generalHandStrings) {
    let generalHands = [];
    let generalHand;

    generalHandStrings.forEach(function (generalHandString) {
      generalHand = new GeneralHand(generalHandString);
      generalHands.push(generalHand);
    });

    return generalHands;
  };
  initializeAsBelongingToPlayerStateOtherThanFirstPlayerState() {
    this.forEachSpecificHand(function (specificHand) {
      specificHand.manuallySet = false;
      specificHand.maxPossibleWeight = 0;
    });
    this.numberOfPossibleSpecificHands = 0;
  };

  // iterators
  forEach(cb, thisValue) {
    this._data.forEach(function (generalHand, index) {
      cb.call(thisValue, generalHand, index);
    });
  };
  forEachPossibleGeneralHand(cb) {
    this._data.forEach(function (generalHand) {
      if (generalHand.possible) {
        cb(generalHand);
      }
    });
  };
  forEachGeneralHandInRange(cb) {
    this._data.forEach(function (generalHand) {
      if (generalHand.inRange) {
        cb(generalHand);
      }
    });
  };
  forEachSpecificHand(cb, thisValue) {
    this.forEach(function (generalHand) {
      generalHand.forEachSpecificHand(function (specificHand) {
        cb.call(thisValue, specificHand, specificHand.string);
      });
    });
  };
  forEachPossibleSpecificHand(cb, thisValue) {
    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible) {
        cb.call(thisValue, specificHand);
      }
    });
  };
  forEachSpecificHandInRange(cb, thisValue) {
    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible && specificHand.inRange) {
        cb.call(thisValue, specificHand);
      }
    });
  };
  forEachPossibleSpecificHandNotInRange(cb, thisValue) {
    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible && !specificHand.inRange) {
        cb.call(thisValue, specificHand);
      }
    });
  };

  // find
  find(handString) {
    if (handString.length === 4) {
      return this._findSpecificHand(handString);
    } else if (handString.length === 3 || handString.length === 2) {
      return this._findGeneralHand(handString);
    } else {
      throw new Error('handString is invalid.');
    }
  };
  _findGeneralHand(generalHandString) {
    let index = constants.generalHandStringToIndexMap[generalHandString];

    if (index === undefined) {
      throw new Error('Unable to find a general hand.');
    }

    if (this._data[index].string !== generalHandString) {
      throw new Error('Found the wrong general hand.');
    }

    return this._data[index];
  };
  _findSpecificHand(specificHandString) {
    specificHandString = utilities.hands.getNormalizedSpecificHandString(specificHandString);
    let generalHand = this.__getGeneralHandFromSpecificHandString(specificHandString);
    let specificHands = generalHand.specificHands;

    for (const combo in specificHands) {
      if (specificHands.hasOwnProperty(combo)) {
        if (specificHands[combo].string === specificHandString) {
          return specificHands[combo];
        }
      }
    }

    throw new Error('Unable to find specific hand.');
  };
  __getGeneralHandFromSpecificHandString(specificHandString) {
    if (specificHandString.length !== 4) {
      throw new Error('specificHandString is invalid.');
    }

    let generalHandString = utilities.hands.getGeneralHandStringFromSpecificHandString(specificHandString);

    return this._findGeneralHand(generalHandString);
  };
  findByIndex(index) {
    return this._data[index];
  };

  // text
  get inRangeText() {
    return getTextFromGeneralHands.inRange(this);
  };
  get manuallyAddedText() {
    return getTextFromGeneralHands.manuallyAdded(this);
  };
  get manuallyRemovedText() {
    return getTextFromGeneralHands.manuallyRemoved(this);
  };

  // manually set
  clearManuallyAddedHands() {
    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.manuallyAdded) {
        specificHand.manuallySet = false;
      }
    });
  };
  clearManuallyRemovedHands() {
    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.manuallyRemoved) {
        specificHand.manuallySet = false;
      }
    });
  };
  clearManuallySetHandsThatPassThroughFilter(fiveCardHand, presetCommunityCardsArray, shortDeck) {
    this.forEachPossibleSpecificHand(function (specificHand) {
      specificHand.clearManuallySetIfPassesThroughFilter(fiveCardHand, presetCommunityCardsArray, shortDeck);
    });
  };

  // weighted range
  get weightedRange() {
    let usedHandWeights = this._constructArrayOfUsedHandWeights();
    let maxUsedHandWeight = this._getMaxUsedHandWeight(usedHandWeights);
    let maxNumberOfCopiesOfSpecificHandForWeightedRange = this._getMaxNumberOfCopiesOfSpecificHandForWeightedRange(usedHandWeights, maxUsedHandWeight);
    let weightedRange = [];
    let numberOfCopiesOfSpecificHandForWeightedRange;

    this.forEachSpecificHandInRange(function (specificHand) {
      numberOfCopiesOfSpecificHandForWeightedRange = maxNumberOfCopiesOfSpecificHandForWeightedRange * (specificHand.weight / maxUsedHandWeight);
      _.times(numberOfCopiesOfSpecificHandForWeightedRange, function () {
        weightedRange.push([ specificHand.firstCard, specificHand.secondCard ]);
      });
    });

    return weightedRange;
  };
  _constructArrayOfUsedHandWeights() {
    let usedHandWeights = [];

    this.forEachSpecificHandInRange(function (specificHand) {
      if (!usedHandWeights.includes(specificHand.weight)) {
        usedHandWeights.push(specificHand.weight);
      }
    });

    return usedHandWeights;
  };
  _getMaxUsedHandWeight(usedHandWeights) {
    return Math.max.apply(null, usedHandWeights);
  };
  _getMaxNumberOfCopiesOfSpecificHandForWeightedRange(usedHandWeights, maxUsedHandWeight) {
    if (usedHandWeights.length === 1) {
      return 1;
    }

    let gcdOfUsedHandWeights = this.__getGcdOfList(usedHandWeights);

    return maxUsedHandWeight / gcdOfUsedHandWeights;
  };
  __getGcdOfList(list) {
    return list.reduce(this.___getGcd);
  };
  ___getGcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    if (b > a) {var temp = a; a = b; b = temp;}
    while (true) {
      if (b == 0) return a;
      a %= b;
      if (a == 0) return b;
      b %= a;
    }
  };

  // value to bluff ratio
  getPercentOfSelectedRangeThatIsValue(idOfCutoffHand, communityCards) {
    let weightedNumberOfCombosThatAreValue = 0;
    let madeHandIds = [
      'fourOfAKindPlus:royalFlush', 'fourOfAKindPlus:straightFlush', 'fourOfAKindPlus:fourOfAKind', 'fullHouse', 'flush:nut', 'flush:secondNut', 'flush:thirdNut', 'flush:weak', 'straight',
      'threeOfAKind:topSet', 'threeOfAKind:secondSet', 'threeOfAKind:thirdSet', 'threeOfAKind:fourthSet', 'threeOfAKind:middleSet', 'threeOfAKind:bottomSet', 'threeOfAKind:trips', 'twoPair:usingTopBoardCard', 'twoPair:weak',
      'pair:overpair', 'pair:tptk', 'pair:tpgk', 'pair:tpwk', 'pair:ppUnderTp', 'pair:secondPair', 'pair:middlePair', 'pair:weakPair', 'highCard:aceHigh'
    ];
    let holeCards;
    let handInfo;
    let isValueHand;

    this.forEachSpecificHandInRange(function (specificHand) {
      holeCards = [specificHand.firstCard, specificHand.secondCard];
      handInfo = getHandInfo.run(_.union(holeCards, communityCards));
      setSpecificHandInfo.run(handInfo, holeCards, communityCards);
      isValueHand = madeHandIds.includes(handInfo.id) && (madeHandIds.indexOf(handInfo.id) <= madeHandIds.indexOf(idOfCutoffHand));

      if (isValueHand) {
        weightedNumberOfCombosThatAreValue += (specificHand.weight / 100);
      }
    });

    return ((weightedNumberOfCombosThatAreValue / this.numberOfSpecificHandsInRange) * 100).roundToTwoDecimalPlaces();
  };

  // five card hand highlights
  highlightMatchingGeneralHands(specificHandStrings) {
    this.forEachPossibleGeneralHand(function (generalHand) {
      generalHand.highlightIfMatchesSpecificHandStringList(specificHandStrings);
    });
  };
  removeFiveCardHandHighlights() {
    this.forEach(function (generalHand) {
      generalHand.matchesFiveCardHandHover = false;
    });
  };

  // short deck
  adjustForShortDeck() {
    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.hasFiveFourThreeOrTwo) {
        this.numberOfPossibleSpecificHands -= (specificHand.maxPossibleWeight / 100);
        specificHand.maxPossibleWeight = 0;
      }
    }, this);
  };

  // other
  matchesOtherRange(otherGeneralHands, hack) {
    if (this._data.length !== 169 || otherGeneralHands._data.length !== 169) {
      throw new Error('One or more ranges are invalid.');
    }

    if (
      (this.numberOfSpecificHandsInRange >= 0.05 || otherGeneralHands.numberOfSpecificHandsInRange >= 0.05) &&
      (this.numberOfSpecificHandsInRange !== otherGeneralHands.numberOfSpecificHandsInRange)
    ) {
      return false;
    }

    if (
      (this.numberOfPossibleSpecificHands >= 0.05 || otherGeneralHands.numberOfPossibleSpecificHands >= 0.05) &&
      (this.numberOfPossibleSpecificHands !== otherGeneralHands.numberOfPossibleSpecificHands)
    ) {
      return false;
    }

    let specificHandOfThisRange;
    let specificHandOfOtherRange;

    // doing this as a hack because areFiveCardHandPercentagesUpToDate in RangeForStreet creates lag, and the downside is small
    if (!hack) {
      for (let i = 0; i < this._data.length; i++) {
        for (let specificHandSuits in this._data[i].specificHands) {
          specificHandOfThisRange = this.findByIndex(i).specificHands[specificHandSuits];
          specificHandOfOtherRange = otherGeneralHands.findByIndex(i).specificHands[specificHandSuits];

          if (specificHandOfThisRange._maxPossibleWeight !== specificHandOfOtherRange._maxPossibleWeight) {
            return false;
          }

          if (specificHandOfThisRange._weight !== specificHandOfOtherRange._weight) {
            return false;
          }

          if (specificHandOfThisRange._manuallySet !== specificHandOfOtherRange._manuallySet) {
            return false;
          }
        }
      }
    }

    return true;
  };
  get percentOfHandsInRangeNumberForDisplay() {
    if (this.numberOfSpecificHandsInRange < 0.05) {
      return 0;
    }

    let exactPercentOfHandsInRange = (this.numberOfSpecificHandsInRange / this.numberOfPossibleSpecificHands) * 100;

    return exactPercentOfHandsInRange.roundToOneDecimalPlace();
  };
};



// WEBPACK FOOTER //
// ./src/services/hands/GeneralHands.js